import { useEffect, useMemo, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Palette, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";

const PRESETS = ["#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c", "#0891b2", "#db2777", "#65a30d",
  "#4f46e5", "#b45309", "#1e293b", "#c026d3", "#1d4ed8", "#be123c", "#15803d", "#7c3aed"];

const StaffRow = ({ u, canEdit, onSave }) => (
  <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`colour-row-${u.id}`}>
    <span className="h-8 w-8 rounded-md shrink-0 border border-black/10" style={{ backgroundColor: u.displayColour }} data-testid={`colour-swatch-${u.id}`} />
    <div className="min-w-[160px] flex-1">
      <p className="text-sm font-medium text-slate-900">{u.displayName}</p>
      <p className="text-xs text-slate-500">{u.calendarColour ? "Chosen colour" : "Automatic colour"}</p>
    </div>
    {canEdit && (
      <div className="flex flex-wrap items-center gap-1.5">
        {PRESETS.map((c) => (
          <button key={c} type="button" title={c} onClick={() => onSave(u, c)}
            className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${u.displayColour === c ? "border-slate-900" : "border-white shadow"}`}
            style={{ backgroundColor: c }} data-testid={`colour-preset-${u.id}-${c.slice(1)}`} />
        ))}
        <input type="color" value={u.displayColour || "#2563eb"} onChange={(e) => onSave(u, e.target.value)}
          className="h-7 w-9 cursor-pointer rounded border border-slate-200 bg-white" title="Pick any colour" data-testid={`colour-picker-${u.id}`} />
        {u.calendarColour && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onSave(u, "")} data-testid={`colour-reset-${u.id}`}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />Auto
          </Button>
        )}
      </div>
    )}
  </div>
);

export default function StaffColours() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const canEdit = can("user.manage");

  useEffect(() => {
    client.get("/users").then(({ data }) => setUsers(data.filter((u) => u.status !== "deactivated")))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, []);

  const list = useMemo(() => users
    .filter((u) => (u.displayName || "").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || "")), [users, q]);

  const save = async (u, colour) => {
    try {
      const { data } = await client.patch(`/users/${u.id}`, { calendarColour: colour });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="staff-colours-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Palette className="h-6 w-6 text-brand" />Staff Colours</h1>
        <p className="text-sm text-slate-500 mt-1">Field jobs on the schedule show the colour of the first cleaner assigned. Unassigned jobs show in light grey. Connect site shifts keep one colour.</p>
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" className="pl-8" data-testid="staff-colours-search" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="staff-colours-list">
        {list.map((u) => <StaffRow key={u.id} u={u} canEdit={canEdit} onSave={save} />)}
        {!list.length && <p className="p-6 text-sm text-slate-500">No staff found.</p>}
      </div>
    </div>
  );
}
